import { useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, TileLayer, useMap } from "react-leaflet";
import { GEO_RISK_HEXAGONS } from "./geoRiskMapData";

const OSM_STANDARD_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const CARTO_LIGHT_URL = "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png";
const FULL_GEOJSON_URL = "/map_runtime/data/geo_risk_2024.geojson";

const MAP_METRICS = {
  predictedCount: {
    label: "Прогнозное число ДТП",
    shortLabel: "Прогнозное число ДТП",
    property: "predicted_accident_count",
    format: (value) => Number(value).toFixed(4),
    palette: ["#7a003c", "#c51b4a", "#f45132", "#ff9f3a", "#fff6b0"],
  },
  observedAccidents: {
    label: "Фактические ДТП",
    shortLabel: "Фактические ДТП",
    property: "observed_accident_count",
    format: (value) => String(value),
    palette: ["#eff6ff", "#93c5fd", "#3b82f6", "#1d4ed8", "#172554"],
  },
  fatalAccidents: {
    label: "Смертельные ДТП",
    shortLabel: "Смертельные ДТП",
    property: "observed_fatal_accident_count",
    format: (value) => String(value),
    palette: ["#fff1f2", "#fda4af", "#fb7185", "#e11d48", "#881337"],
  },
};

function metricColor(value, maxValue, palette) {
  const level = maxValue > 0 ? value / maxValue : 0;
  return palette[Math.min(palette.length - 1, Math.floor(level * palette.length))];
}

function RemoveLeafletAttribution() {
  const map = useMap();

  useEffect(() => {
    map.attributionControl?.remove();
  }, [map]);

  return null;
}

function RiskMap({ metricId, onRangeChange }) {
  const [mapState, setMapState] = useState({ loading: true, error: "", data: null });
  const [tileUrl, setTileUrl] = useState(OSM_STANDARD_URL);
  const metric = MAP_METRICS[metricId];

  useEffect(() => {
    let cancelled = false;
    fetch(FULL_GEOJSON_URL)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then((data) => {
        const count = data?.features?.length;
        if (count !== 6000) throw new Error(`Expected 6000 H3, got ${count}`);
        if (!cancelled) setMapState({ loading: false, error: "", data });
      })
      .catch(() => {
        if (!cancelled) setMapState({ loading: false, error: "Не удалось загрузить полную витрину карты риска.", data: null });
      });
    return () => { cancelled = true; };
  }, []);

  const range = useMemo(() => {
    if (!mapState.data?.features?.length) return { min: 0, max: 0 };
    const values = mapState.data.features.map(({ properties }) => Number(properties[metric.property]) || 0);
    return { min: Math.min(...values), max: Math.max(...values) };
  }, [mapState.data, metric]);

  useEffect(() => { onRangeChange(range); }, [onRangeChange, range]);

  const style = (feature) => {
    const value = Number(feature.properties[metric.property]) || 0;
    return {
      color: "#5c102f",
      weight: 0.18,
      fillColor: metricColor(value, range.max, metric.palette),
      fillOpacity: value === 0 ? 0.16 : 0.7,
    };
  };

  const bindTooltip = (feature, layer) => {
    const properties = feature.properties;
    const value = Number(properties[metric.property]) || 0;
    layer.bindTooltip(
      `<strong>H3: ${properties.hex_id}</strong><br>${metric.label}: ${metric.format(value)}<br>Класс риска: ${properties.count_risk_class_ru}<br>Фактические ДТП: ${properties.observed_accident_count}<br>Смертельные ДТП: ${properties.observed_fatal_accident_count}`,
      { sticky: true, direction: "top", opacity: 0.96 }
    );
  };

  if (mapState.loading) return <div className="statistics-geo-risk-map-state">Загружаем полную витрину: 6 000 H3…</div>;
  if (mapState.error) return <div className="statistics-geo-risk-map-state is-error">{mapState.error}</div>;

  return <MapContainer className="statistics-geo-risk-map" center={[55.75, 37.62]} zoom={9.5} minZoom={8} scrollWheelZoom attributionControl={false}>
    <RemoveLeafletAttribution />
    <TileLayer url={tileUrl} eventHandlers={{ tileerror: () => setTileUrl((current) => current === OSM_STANDARD_URL ? CARTO_LIGHT_URL : current) }} />
    <GeoJSON key={metricId} data={mapState.data} style={style} onEachFeature={bindTooltip} />
  </MapContainer>;
}

export default function GeoRiskReportSection() {
  const [metricId, setMetricId] = useState("predictedCount");
  const [range, setRange] = useState({ min: 0, max: 0 });
  const metric = MAP_METRICS[metricId];
  const topHexagons = GEO_RISK_HEXAGONS.slice(0, 20);

  return <section className="statistics-geo-risk-report">
    <header className="statistics-geo-risk-hero">
      <h2>Карта прогнозного риска ДТП</h2>
      <p>На карту встроены 6 000 дорожных H3 с наибольшим прогнозным числом ДТП. Переключатель внутри карты меняет показатель между прогнозным числом ДТП, фактическими ДТП и смертельными ДТП.</p>
    </header>

    <div className="statistics-geo-risk-summary-grid">
      <article><span>Показано H3</span><strong>6 000</strong><small>Наиболее рисковый сегмент</small></article>
      <article><span>Дорожных H3 всего</span><strong>106 517</strong><small>Полный аналитический реестр</small></article>
      <article><span>ДТП в дорожной области</span><strong>8 874</strong><small>2024 год</small></article>
      <article><span>Смертельные ДТП</span><strong>325</strong><small>В дорожной области</small></article>
      <article><span>Вне дорожного слоя</span><strong>27</strong><small>24 H3 с mismatch</small></article>
    </div>

    <div className="statistics-geo-risk-map-heading"><h3>Пространственное распределение</h3><small>Подложка OpenStreetMap требует интернет-соединения</small></div>
    <div className="statistics-geo-risk-map-shell">
      <label className="statistics-geo-risk-map-control">Показатель
        <select value={metricId} onChange={(event) => setMetricId(event.target.value)}>{Object.entries(MAP_METRICS).map(([id, item]) => <option key={id} value={id}>{item.label}</option>)}</select>
      </label>
      <RiskMap metricId={metricId} onRangeChange={setRange} />
      <aside className="statistics-geo-risk-legend" aria-label="Легенда карты"><strong>{metric.shortLabel}</strong><div className={`statistics-geo-risk-gradient is-${metricId}`} /><div><span>{metric.format(range.min)}</span><span>{metric.format(range.max)}</span></div><small>Наведите курсор на гексагон, чтобы увидеть исходные значения.</small></aside>
    </div>

    <div className="statistics-geo-risk-concentration">
      <article><h3>Очень высокий риск</h3><p><strong>20% дорожных H3</strong> концентрируют около <strong>74,7%</strong> фактических ДТП и <strong>69,2%</strong> смертельных ДТП.</p></article>
      <article><h3>Высокий + очень высокий</h3><p><strong>40% дорожных H3</strong> концентрируют около <strong>93,1%</strong> всех ДТП и <strong>89,8%</strong> смертельных ДТП.</p></article>
      <article className="is-note"><h3>Как читать карту</h3><p>Прогнозный риск — ожидаемая частота с учётом инфраструктуры, а не гарантия происшествия и не причинная оценка.</p></article>
    </div>

    <div className="statistics-report-section-heading"><h3>Топ H3 по прогнозному числу ДТП</h3><p>Первые 20 ячеек из той же высокорисковой выборки; сортировка зафиксирована по прогнозному годовому числу ДТП.</p></div>
    <div className="statistics-report-table-wrap"><table className="statistics-report-table statistics-geo-risk-table"><thead><tr><th>H3</th><th>Класс риска</th><th>Прогнозное число ДТП</th><th>Вероятность</th><th>Фактические ДТП</th><th>Смертельные ДТП</th></tr></thead><tbody>{topHexagons.map((row) => <tr key={row.hexId}><th>{row.hexId}</th><td>Очень высокий</td><td>{row.predictedCount.toFixed(4)}</td><td>{row.probability.toFixed(4)}</td><td>{row.observedAccidents}</td><td>{row.fatalAccidents}</td></tr>)}</tbody></table></div>
    <p className="statistics-geo-risk-source-note">Источник: полный GeoJSON, извлечённый из <code>temp_reposit/DTP_STATIC_HTML_REPORT_V2/07_geo_risk.html</code>. Карта загружается только при открытии вкладки.</p>
  </section>;
}
